import React, { useState, useRef, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { useGameStore } from '../store/gameStore';
import Tower from './Tower';
import SchoolMap from './SchoolMap';
import Enemy from './Enemy';
import Path from './Path';
import JokeCharacter from './JokeCharacter';
import { Vector3 } from 'three';

// School-related jokes for the joke character
const jokes = [
  "Why did the math book look sad? Because it had too many problems!",
  "What did the science teacher say to the student? 'You're making great progress, but you're still a bit of a solution!'",
  "Why did the art student go to the doctor? Because they had too many sketchy ideas!",
  "What's a drama teacher's favorite type of music? Show tunes!",
  "Why did the student eat their homework? Because the teacher said it was a piece of cake!",
  "What did the computer say to the student? 'You've got a byte to learn!'",
  "Why did the student bring a ladder to school? Because they wanted to go to high school!",
  "What's a teacher's favorite nation? Expla-nation!",
  "Why did the student do multiplication problems on the floor? Because the teacher told them not to use tables!",
  "What did the student say when they got an A+? 'This is grade-A work!'"
];

const GameLevel: React.FC = () => {
  const { 
    isPlaying,
    isPaused,
    towers, 
    path, 
    enemies, 
    selectedTowerType, 
    wave,
    difficulty,
    addTower, 
    damageEnemy,
    spawnEnemy,
    updateEnemyPosition
  } = useGameStore();

  const [hoverPosition, setHoverPosition] = useState<Vector3 | null>(null);
  const [canPlace, setCanPlace] = useState(true);
  const gameTime = useRef(0);
  const lastSpawnTime = useRef(0);
  const waveConfig = useRef({
    aliens: 0,
    ufos: 0,
    bosses: 0,
    spawnInterval: 2,
  });
  
  // Configure wave based on wave number
  React.useEffect(() => {
    if (wave > 0) {
      // Define difficulty multiplier
      const difficultyMultiplier = (() => {
        switch (difficulty) {
          case 'easy': return 0.7;  // Fewer enemies, slower spawns
          case 'normal': return 1.0; // Standard
          case 'hard': return 1.2;  // More enemies, faster spawns
          case 'insane': return 1.8; // Significantly more enemies
          default: return 1.0;
        }
      })();
      
      waveConfig.current = {
        aliens: Math.min(5 + wave * 2 * difficultyMultiplier, 40),
        ufos: Math.max(0, Math.min(wave - 2, 15) * difficultyMultiplier),
        bosses: Math.max(0, Math.floor((wave - 5) / 2) * difficultyMultiplier),
        spawnInterval: Math.max(0.3, 2 - wave * 0.1 - (1 - difficultyMultiplier)),
      };
    }
  }, [wave, difficulty]);
  
  // Check for portal parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const portal = urlParams.get('portal');
    const ref = urlParams.get('ref');

    if (portal === 'true' && ref) {
      // If player came through a portal, spawn a portal tower
      const spawnPosition = new Vector3(0, 0, 0); // You can adjust this position
      addTower('portal', spawnPosition);
      
      // Start the game immediately
      useGameStore.getState().startGame();
    }
  }, []);
  
  // Handle placing towers
  const handleMapClick = (position: Vector3) => {
    console.log('Attempting to place tower:', {
      selectedTowerType,
      canPlace,
      position,
      isPlaying,
      isPaused,
      resources: useGameStore.getState().resources
    });
    
    if (selectedTowerType && canPlace) {
      console.log('Placing tower of type:', selectedTowerType);
      addTower(selectedTowerType, position);
    } else {
      console.log('Cannot place tower:', {
        selectedTowerType: selectedTowerType ? 'yes' : 'no',
        canPlace,
        isPlaying,
        isPaused
      });
    }
  };

  // Check if tower can be placed at hover position
  const handleMapHover = (position: Vector3) => {
    console.log('Hover position:', position);
    setHoverPosition(position);
    
    // Check if position is too close to path
    const isTooCloseToPath = path.some(
      pathPoint => pathPoint.distanceTo(position) < 2
    );
    
    // Check if position is already occupied by another tower
    const isOccupied = towers.some(
      tower => tower.position.distanceTo(position) < 2
    );
    
    console.log('Placement checks:', {
      isTooCloseToPath,
      isOccupied,
      canPlace: !isTooCloseToPath && !isOccupied
    });
    
    setCanPlace(!isTooCloseToPath && !isOccupied);
  };
  
  // Game loop
  useFrame((_, delta) => {
    if (!isPlaying || isPaused) return;

    // Tower attacks
    towers.forEach(tower => {
      // Find enemies in range
      const enemiesInRange = enemies.filter(
        enemy => enemy.position.distanceTo(tower.position) <= tower.range
      );
      
      if (enemiesInRange.length > 0) {
        // Attack the first enemy in range (closest to the end of the path)
        const target = enemiesInRange.sort((a, b) => b.pathIndex - a.pathIndex)[0];
        damageEnemy(target.id, tower.damage, tower);
      }
    });

    // Enemy spawning and movement
    gameTime.current += delta;

    // Spawn enemies
    if (
      gameTime.current - lastSpawnTime.current > waveConfig.current.spawnInterval &&
      (waveConfig.current.aliens > 0 || waveConfig.current.ufos > 0 || waveConfig.current.bosses > 0)
    ) {
      lastSpawnTime.current = gameTime.current;

      // Determine which enemy type to spawn
      let enemyType = '';
      if (waveConfig.current.bosses > 0 && Math.random() < 0.1) {
        enemyType = 'boss';
        waveConfig.current.bosses--;
      } else if (waveConfig.current.ufos > 0 && Math.random() < 0.3) {
        enemyType = 'ufo';
        waveConfig.current.ufos--;
      } else if (waveConfig.current.aliens > 0) {
        enemyType = 'alien';
        waveConfig.current.aliens--;
      }

      if (enemyType) {
        spawnEnemy(enemyType);
      }
    }

    // Move enemies along path
    enemies.forEach(enemy => {
      const nextPoint = path[Math.min(enemy.pathIndex + 1, path.length - 1)];
      
      const direction = new Vector3().subVectors(nextPoint, enemy.position).normalize();
      const adjustedSpeed = enemy.speed * enemy.freezeMultiplier;
      const newPosition = enemy.position.clone().add(direction.multiplyScalar(adjustedSpeed));
      
      // Check if we've reached the next point
      const distanceToNext = newPosition.distanceTo(nextPoint);
      if (distanceToNext < 0.2) {
        updateEnemyPosition(enemy.id, nextPoint.clone(), enemy.pathIndex + 1);
      } else {
        updateEnemyPosition(enemy.id, newPosition, enemy.pathIndex);
      }
    });
  });
  
  return (
    <>
      {/* School map with click and hover handlers */}
      <SchoolMap onClick={handleMapClick} onHover={handleMapHover} />
      
      {/* Path visualization */}
      <Path points={path} />
      
      {/* Enemies */}
      {enemies.map(enemy => (
        <Enemy key={enemy.id} enemy={enemy} />
      ))}
      
      {/* Towers */}
      {towers.map(tower => (
        <Tower key={tower.id} tower={tower} />
      ))}
      
      {/* Tower placement preview */}
      {selectedTowerType && hoverPosition && (
        <mesh position={hoverPosition} rotation={[0, 0, 0]}>
          <cylinderGeometry args={[1, 1, 0.1, 32]} />
          <meshStandardMaterial 
            color={canPlace ? 'green' : 'red'} 
            transparent 
            opacity={0.5} 
          />
        </mesh>
      )}

      {/* Joke Character */}
      <JokeCharacter jokes={jokes} />
    </>
  );
};

export default GameLevel; 